import { useState } from "react";
import { Loader2 } from "lucide-react";
import { DashboardPanel } from "@/components/building/DashboardPanel";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import {
  useBillingMoney,
  useBillingOverview,
  useBillingPlans,
  useBuildingSubscription,
} from "../hooks";
import type { BillingOverview, BuildingPlanTier } from "../types";
import {
  buildingQuoteTimingCopy,
  paidBuildingCreationBlock,
  quoteForBuildingPlan,
} from "../buildingPlanCreation";
import { BuildingPlanChoice } from "./BuildingPlanChoice";

/**
 * What the organisation is about to be charged, and the button that agrees to it.
 *
 * WHY THIS IS ONE COMPONENT. A building is the unit of billing, and until this
 * existed exactly ONE of the ways to create one said so: the Add building dialog
 * on the Billing overview quoted the rate and the prorated amount, while the
 * Buildings page's "Add New Building" — the way almost everyone actually adds
 * one — created a billable building silently. Restoring an archived building did
 * the same. A price shown on one path and hidden on three is not a price the
 * customer agreed to, so every path now goes through this component and the
 * Billing overview's own dialog renders the same quote block rather than a
 * second copy of the wording.
 */

/** Whether this is a fresh building or one coming back out of the archive. */
export type BuildingCostIntent =
  | { mode: "add"; choosePlan?: boolean }
  | { mode: "restore"; buildingId: number };

type Quote = BillingOverview["add_building_quote"];

/**
 * A restore inside the period the building was removed in costs nothing extra —
 * that period is paid for and the service row is still open, which is the same
 * rule `billing_building_service_one_open` enforces in the database. A restore in
 * a LATER period is an ordinary prorated add.
 *
 * The removal date is read off the overview's own removed list rather than
 * carried in by the caller: the Buildings page knows a row is archived but not
 * when, and this RPC is already being fetched for the quote.
 *
 * ponytail: a building archived directly on the Buildings page has no removed
 * row, so it falls through to the prorated quote — the statement that may
 * over-state a same-period restore, never under-state one. Carrying
 * `buildings.archived_at` through the list query is the upgrade if that matters.
 */
function isAlreadyBilledThisPeriod(intent: BuildingCostIntent, overview: BillingOverview): boolean {
  if (intent.mode !== "restore") return false;
  const removed = overview.removed_buildings.find((row) => row.building_id === intent.buildingId);
  return removed !== undefined && removed.removed_on >= overview.period_start;
}

export function BuildingCostQuote({ quote, currency }: { quote: Quote; currency: string }) {
  const { t } = useLanguage();
  const money = useBillingMoney();
  const copy = t.billing.consent;
  return (
    <DashboardPanel>
      <p className="text-sm text-muted-foreground">{copy.quoteLead}</p>
      <p className="mt-1 text-2xl font-semibold tracking-tight">
        + {money(quote.monthly_rate_cents, currency)} {t.billing.rate.perBuildingMonth}
      </p>
      <p className="mt-1 text-xs text-muted-foreground">
        {quote.monthly_rate_cents === 0
          ? t.billing.plans.noCard
          : fill(buildingQuoteTimingCopy(quote, t), {
              days: String(quote.billable_days),
              amount: money(quote.amount_cents, currency),
            })}
      </p>
    </DashboardPanel>
  );
}

function NoChargePanel() {
  const copy = useLanguage().t.billing.consent;
  return (
    <DashboardPanel>
      <p className="text-sm text-muted-foreground">{copy.restoreSamePeriodLead}</p>
      <p className="mt-1 text-lg font-semibold tracking-tight">{copy.restoreSamePeriod}</p>
    </DashboardPanel>
  );
}

interface ConsentDialogProps {
  open: boolean;
  intent: BuildingCostIntent;
  onCancel: () => void;
  onAgree: (tier?: BuildingPlanTier) => void;
}

function ConsentQuoteUnavailable({ onCancel, retry }: { onCancel: () => void; retry: () => void }) {
  const copy = useLanguage().t.billing.consent;
  return (
    <>
      <p className="py-4 text-sm text-destructive">{copy.quoteUnavailable}</p>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {copy.cancel}
        </Button>
        <Button type="button" onClick={retry}>
          {copy.retry}
        </Button>
      </DialogFooter>
    </>
  );
}

function restoreQuoteUnavailable(
  restoring: boolean,
  subscription: ReturnType<typeof useBuildingSubscription>
) {
  return restoring && (subscription.isError || !subscription.data);
}

function useConsentPlan(intent: BuildingCostIntent) {
  const choosePlan = intent.mode === "add" && intent.choosePlan === true;
  const plans = useBillingPlans(choosePlan);
  const [tier, setTier] = useState<BuildingPlanTier>("free");
  const restoring = intent.mode === "restore";
  const subscription = useBuildingSubscription(restoring ? intent.buildingId : 0, restoring);
  const plan = choosePlan
    ? plans.data?.find((entry) => entry.code === tier)
    : subscription.data?.plan;
  const unavailable = choosePlan
    ? !plan || plans.isError
    : restoreQuoteUnavailable(restoring, subscription);
  const retry = () => {
    void plans.refetch();
    if (restoring) void subscription.refetch();
  };
  return { choosePlan, plans, tier, setTier, plan, unavailable, retry };
}

function ConsentPlanSelection({ selection }: { selection: ReturnType<typeof useConsentPlan> }) {
  const { t } = useLanguage();
  if (!selection.choosePlan) return null;
  if (selection.plans.data)
    return (
      <BuildingPlanChoice
        plans={selection.plans.data}
        tier={selection.tier}
        onChange={selection.setTier}
      />
    );
  return (
    <p className="text-sm text-muted-foreground">
      {selection.plans.isError ? t.billing.plans.unavailable : t.billing.consent.loading}
    </p>
  );
}

function ConsentQuote({
  noCharge,
  unavailable,
  quote,
  currency,
}: {
  noCharge: boolean;
  unavailable: boolean;
  quote: Quote;
  currency: string;
}) {
  const copy = useLanguage().t.billing.consent;
  if (noCharge) return <NoChargePanel />;
  if (unavailable) return <p className="text-sm text-muted-foreground">{copy.quoteUnavailable}</p>;
  return <BuildingCostQuote quote={quote} currency={currency} />;
}

function ConsentActions({
  intent,
  onAgree,
  onCancel,
  selection,
  reason,
}: Omit<ConsentDialogProps, "open"> & {
  selection: ReturnType<typeof useConsentPlan>;
  reason?: string;
}) {
  const copy = useLanguage().t.billing.consent;
  return (
    <DialogFooter className="pt-1">
      <Button type="button" variant="outline" onClick={onCancel}>
        {copy.cancel}
      </Button>
      {selection.unavailable && (
        <Button variant="outline" onClick={selection.retry}>
          {copy.retry}
        </Button>
      )}
      <Button
        type="button"
        onClick={() => onAgree(selection.choosePlan ? selection.tier : undefined)}
        disabled={Boolean(reason) || Boolean(selection.unavailable)}
      >
        {intent.mode === "restore" ? copy.agreeRestore : copy.agreeAdd}
      </Button>
    </DialogFooter>
  );
}

function ConsentDialogBody({ intent, onAgree, onCancel }: Omit<ConsentDialogProps, "open">) {
  const { t } = useLanguage();
  const copy = t.billing.consent;
  // Radix unmounts a closed dialog's content, so this body — and the overview
  // RPC it reads — exists only while the question is actually being asked. The
  // Buildings page must not fetch the whole period's charge list for every
  // visitor who never adds anything.
  const overview = useBillingOverview();
  const selection = useConsentPlan(intent);

  if (overview.isLoading) {
    return (
      <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {copy.loading}
      </p>
    );
  }
  if (overview.isError || !overview.data) {
    return <ConsentQuoteUnavailable onCancel={onCancel} retry={() => void overview.refetch()} />;
  }

  const data = overview.data;
  const reason = selection.choosePlan
    ? paidBuildingCreationBlock(data.context, selection.tier, t)
    : undefined;
  return (
    <>
      <ConsentPlanSelection selection={selection} />
      <ConsentQuote
        noCharge={isAlreadyBilledThisPeriod(intent, data)}
        unavailable={selection.unavailable}
        quote={quoteForBuildingPlan(data.add_building_quote, selection.plan)}
        currency={data.context.currency}
      />
      {reason && (
        <p role="status" className="text-sm text-destructive">
          {reason}
        </p>
      )}
      <ConsentActions
        intent={intent}
        onAgree={onAgree}
        onCancel={onCancel}
        selection={selection}
        reason={reason}
      />
    </>
  );
}

export function BuildingCostConsentDialog(props: ConsentDialogProps) {
  const copy = useLanguage().t.billing.consent;
  return (
    <Dialog open={props.open} onOpenChange={(open) => (open ? undefined : props.onCancel())}>
      <DialogContent className="max-w-md">
        <DialogHeader className="pb-1">
          <DialogTitle>
            {props.intent.mode === "restore" ? copy.restoreTitle : copy.addTitle}
          </DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>
        <ConsentDialogBody
          intent={props.intent}
          onAgree={props.onAgree}
          onCancel={props.onCancel}
        />
      </DialogContent>
    </Dialog>
  );
}
